'use client'

import type { ReactElement } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import type { SwiperOptions } from 'swiper/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import './swiperCarousel.css'

/**
 * Multi-card carousel track — the Swiper replacement for the `react-slick`
 * sliders (home News, Guides, Promotions).
 *
 * Two things worth knowing before editing a config that uses this:
 *
 * 1. **Breakpoints invert.** react-slick's `responsive[].breakpoint` was a
 *    max-width ceiling (the entry applied at that width and below). Swiper's
 *    `breakpoints` keys are min-width floors. So the component's own props
 *    describe the SMALLEST viewport, and each `breakpoints` entry overrides it
 *    upward. Copying an old slick config across unchanged inverts every rule.
 *
 * 2. **Arrows and dots are ours, not Swiper's.** Rendering them here instead of
 *    via Swiper's Navigation/Pagination modules keeps `swiper/css` as the only
 *    Swiper stylesheet, lets `arrowsClassName` hide arrows per breakpoint with
 *    plain CSS (no JS viewport check, so nothing to get wrong in the static
 *    export), and keeps the disabled state under our control. Geometry lives in
 *    `swiperCarousel.css`.
 */
export interface CardCarouselProps {
  /** Slides. Element keys are preserved onto the generated `SwiperSlide`s. */
  children: ReactElement[]
  /** Min-width overrides. Component props above describe the smallest viewport. */
  breakpoints?: SwiperOptions['breakpoints']
  slidesPerView?: SwiperOptions['slidesPerView']
  slidesPerGroup?: number
  spaceBetween?: number
  centeredSlides?: boolean
  /** Track height follows the active slide — slick's `adaptiveHeight`. */
  autoHeight?: boolean
  speed?: number
  className?: string
  /** Arrow images. Omit for a carousel with no arrows at any width. */
  arrows?: { prevSrc: string; nextSrc: string }
  /** Extra classes on both arrows — use Bootstrap display utils to hide them. */
  arrowsClassName?: string
  showDots?: boolean
  /** Extra classes on the dots list — use Bootstrap display utils to hide them. */
  dotsClassName?: string
}

export function CardCarousel({
  children,
  breakpoints,
  slidesPerView = 1,
  slidesPerGroup = 1,
  spaceBetween = 0,
  centeredSlides = false,
  autoHeight = false,
  speed = 500,
  className = '',
  arrows,
  arrowsClassName = '',
  showDots = false,
  dotsClassName = ''
}: CardCarouselProps) {
  const swiperRef = useRef<SwiperType | null>(null)
  // Dots track Swiper's SNAP grid, not the slide list: with slidesPerGroup > 1
  // one dot represents a page of slides. Reading `snapGrid`/`snapIndex` off the
  // instance keeps that correct per breakpoint — deriving it from the
  // `slidesPerGroup` prop would ignore the `breakpoints` overrides and render
  // one dot per slide (10 instead of slick's 4 on the promotions rail).
  const [snapCount, setSnapCount] = useState(0)
  const [snapIndex, setSnapIndex] = useState(0)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  // Swiper owns the truth for these; mirror it into state so the arrow disabled
  // styling and the active dot re-render. `onBreakpoint` matters because
  // changing slidesPerView can move the track off its end.
  const sync = useCallback((swiper: SwiperType) => {
    setSnapCount(swiper.snapGrid.length)
    setSnapIndex(swiper.snapIndex)
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }, [])

  // Carousels fed by a client-side CMS fetch (the promotions rail) mount with
  // zero slides, where Swiper is legitimately `isEnd` — so the "next" arrow
  // renders disabled. Swiper recomputes correctly when the slides arrive but
  // does not emit an event React sees, so the disabled arrow would stick.
  // Re-reading the instance whenever the slide count changes is what actually
  // closes that gap; the events above never fire for this transition.
  useEffect(() => {
    const swiper = swiperRef.current
    if (swiper && !swiper.destroyed) sync(swiper)
  }, [children.length, sync])

  return (
    <div className={`card-carousel ${className}`.trim()}>
      <Swiper
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        spaceBetween={spaceBetween}
        centeredSlides={centeredSlides}
        autoHeight={autoHeight}
        speed={speed}
        breakpoints={breakpoints}
        watchOverflow
        onSwiper={swiper => {
          swiperRef.current = swiper
          sync(swiper)
        }}
        onSlideChange={sync}
        onBreakpoint={sync}
        onSlidesLengthChange={sync}
        onResize={sync}
      >
        {children.map((child, index) => (
          <SwiperSlide key={child.key ?? index}>{child}</SwiperSlide>
        ))}
      </Swiper>

      {arrows && (
        <>
          <button
            type="button"
            className={`carousel-arrow carousel-arrow--prev ${arrowsClassName}`.trim()}
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={isBeginning}
            aria-label="Previous slide"
          >
            <img src={arrows.prevSrc} alt="" />
          </button>
          <button
            type="button"
            className={`carousel-arrow carousel-arrow--next ${arrowsClassName}`.trim()}
            onClick={() => swiperRef.current?.slideNext()}
            disabled={isEnd}
            aria-label="Next slide"
          >
            <img src={arrows.nextSrc} alt="" />
          </button>
        </>
      )}

      {showDots && snapCount > 1 && (
        <ul className={`carousel-dots ${dotsClassName}`.trim()}>
          {Array.from({ length: snapCount }, (_, index) => (
            <li key={index} className={index === snapIndex ? 'carousel-dot--active' : ''}>
              <button
                type="button"
                onClick={() => {
                  const swiper = swiperRef.current
                  if (swiper) swiper.slideTo(index * swiper.params.slidesPerGroup!)
                }}
                aria-label={`Go to slide ${index + 1}`}
              >
                {index + 1}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CardCarousel
